import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  loadPersistedState,
  savePersistedState,
} from './signal-store-persist.util';

interface IngestProjectFormDraft {
  name: string;
  description: string;
  folder_path: string;
  is_active: boolean;
}

interface IngestProjectsDraftState {
  newProject: IngestProjectFormDraft;
  editingId: number | null;
  editForm: IngestProjectFormDraft;
}

interface IngestUploadDraft {
  companyName: string;
  subfolderPath: string;
  description: string;
}

interface IngestDetailDraft {
  newSubfolderName: string;
}

interface IngestUiState {
  currentRoute: string;
  projectsDraft: IngestProjectsDraftState;
  uploadHubSelectedProjectId: number | null;
  uploadDraftByProject: Record<string, IngestUploadDraft>;
  detailDraftByProject: Record<string, IngestDetailDraft>;
}

type IngestUiLocalState = Pick<
  IngestUiState,
  'currentRoute' | 'projectsDraft' | 'uploadHubSelectedProjectId'
>;
type IngestUiSessionState = Pick<
  IngestUiState,
  'uploadDraftByProject' | 'detailDraftByProject'
>;

const LOCAL_KEY = 'vault_ingest_ui_local_v1';
const SESSION_KEY = 'vault_ingest_ui_session_v1';

const localConfig = { key: LOCAL_KEY, version: 1, storage: 'local' as const };
const sessionConfig = { key: SESSION_KEY, version: 1, storage: 'session' as const };

const emptyProjectDraft: IngestProjectFormDraft = {
  name: '',
  description: '',
  folder_path: '',
  is_active: true,
};

const initialState: IngestUiState = {
  currentRoute: '',
  projectsDraft: {
    newProject: { ...emptyProjectDraft },
    editingId: null,
    editForm: { ...emptyProjectDraft },
  },
  uploadHubSelectedProjectId: null,
  uploadDraftByProject: {},
  detailDraftByProject: {},
};

function isProjectFormDraft(value: unknown): value is IngestProjectFormDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['name'] === 'string' &&
    typeof v['description'] === 'string' &&
    typeof v['folder_path'] === 'string' &&
    typeof v['is_active'] === 'boolean'
  );
}

function isProjectsDraftState(value: unknown): value is IngestProjectsDraftState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const editingId = v['editingId'];
  return (
    isProjectFormDraft(v['newProject']) &&
    isProjectFormDraft(v['editForm']) &&
    (editingId === null || typeof editingId === 'number')
  );
}

function isUploadDraft(value: unknown): value is IngestUploadDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['companyName'] === 'string' &&
    typeof v['subfolderPath'] === 'string' &&
    typeof v['description'] === 'string'
  );
}

function isDetailDraft(value: unknown): value is IngestDetailDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v['newSubfolderName'] === 'string';
}

function isUploadDraftMap(value: unknown): value is Record<string, IngestUploadDraft> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((x) => isUploadDraft(x));
}

function isDetailDraftMap(value: unknown): value is Record<string, IngestDetailDraft> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((x) => isDetailDraft(x));
}

function isLocalState(value: unknown): value is IngestUiLocalState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['currentRoute'] === 'string' &&
    isProjectsDraftState(v['projectsDraft']) &&
    (v['uploadHubSelectedProjectId'] === null || typeof v['uploadHubSelectedProjectId'] === 'number')
  );
}

function isSessionState(value: unknown): value is IngestUiSessionState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return isUploadDraftMap(v['uploadDraftByProject']) && isDetailDraftMap(v['detailDraftByProject']);
}

const persistedInitialState: IngestUiState = {
  ...initialState,
  ...(loadPersistedState(localConfig, isLocalState) ?? {}),
  ...(loadPersistedState(sessionConfig, isSessionState) ?? {}),
};

export const ingestUiStore = signalStore(
  { providedIn: 'root' },
  withState(persistedInitialState),
  withMethods((store) => ({
    setCurrentRoute(route: string): void {
      patchState(store, { currentRoute: route || '' });
      persistLocal(store);
    },
    setProjectsNewDraft(draft: IngestProjectFormDraft): void {
      patchState(store, {
        projectsDraft: {
          ...store.projectsDraft(),
          newProject: { ...draft },
        },
      });
      persistLocal(store);
    },
    setProjectsEditDraft(editingId: number | null, draft: IngestProjectFormDraft): void {
      patchState(store, {
        projectsDraft: {
          ...store.projectsDraft(),
          editingId,
          editForm: { ...draft },
        },
      });
      persistLocal(store);
    },
    clearProjectsEditDraft(): void {
      patchState(store, {
        projectsDraft: {
          ...store.projectsDraft(),
          editingId: null,
          editForm: { ...emptyProjectDraft },
        },
      });
      persistLocal(store);
    },
    setUploadHubSelectedProjectId(projectId: number | null): void {
      patchState(store, { uploadHubSelectedProjectId: projectId });
      persistLocal(store);
    },
    setUploadDraftForProject(projectId: number, draft: IngestUploadDraft): void {
      const key = String(projectId);
      patchState(store, {
        uploadDraftByProject: {
          ...store.uploadDraftByProject(),
          [key]: { ...draft },
        },
      });
      persistSession(store);
    },
    clearUploadDraftForProject(projectId: number): void {
      const key = String(projectId);
      const next = { ...store.uploadDraftByProject() };
      delete next[key];
      patchState(store, { uploadDraftByProject: next });
      persistSession(store);
    },
    setDetailDraftForProject(projectId: number, draft: IngestDetailDraft): void {
      const key = String(projectId);
      patchState(store, {
        detailDraftByProject: {
          ...store.detailDraftByProject(),
          [key]: { ...draft },
        },
      });
      persistSession(store);
    },
    clearDetailDraftForProject(projectId: number): void {
      const key = String(projectId);
      const next = { ...store.detailDraftByProject() };
      delete next[key];
      patchState(store, { detailDraftByProject: next });
      persistSession(store);
    },
  })),
);

function persistLocal(store: {
  currentRoute: () => string;
  projectsDraft: () => IngestProjectsDraftState;
  uploadHubSelectedProjectId: () => number | null;
}): void {
  const state: IngestUiLocalState = {
    currentRoute: store.currentRoute(),
    projectsDraft: store.projectsDraft(),
    uploadHubSelectedProjectId: store.uploadHubSelectedProjectId(),
  };
  savePersistedState(localConfig, state);
}

function persistSession(store: {
  uploadDraftByProject: () => Record<string, IngestUploadDraft>;
  detailDraftByProject: () => Record<string, IngestDetailDraft>;
}): void {
  const state: IngestUiSessionState = {
    uploadDraftByProject: store.uploadDraftByProject(),
    detailDraftByProject: store.detailDraftByProject(),
  };
  savePersistedState(sessionConfig, state);
}

export type IngestUiStore = typeof ingestUiStore;
